import { useEffect, useMemo, useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../@/components/ui/select";
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { Textarea } from "../../@/components/ui/textarea";
import { toast } from "sonner"
import { useAuth } from "@clerk/clerk-react"
import axios from "axios"
import { api_endpoint } from "@/constants/constant"
import { ClipLoader } from "react-spinners";
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, FormField } from "@/components/ui/form"
import { Field, FieldContent, FieldError, FieldLabel } from "../../@/components/ui/field"
import { cn } from "@/lib/utils"


function RoleEditForm({
  item,
  loading,
  onCancel,
  onSave,
}) {
  const schema = useMemo(
    () =>
      z.object({
        role: z.string().min(1, "This is required"),
        description: z.string().min(1, "This is required"),
      }),
    []
  )

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      role: item?.role ?? "",
      description: item?.description ?? "",
    },
  })

  useEffect(() => {
    form.reset({
      role: item?.role ?? "",
      description: item?.description ?? "",
    })
  }, [item, form])

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (values) => {
    setIsSubmitting(true)
    try {
      await onSave({
        ...(item || {}),
        role: values.role,
        description: values.description,
        added_roles: [],
        deleted_roles: [],
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="role"
          render={({ field, fieldState }) => (
            <Field data-disabled={loading || isSubmitting}>
              <FieldLabel>Role *</FieldLabel>
              <FieldContent>
                <Input
                  {...field}
                  placeholder="e.g. ADMIN"
                  className={cn(
                    "font-body",
                    fieldState.error &&
                      "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                  )}
                  disabled={loading || isSubmitting}
                />
                <FieldError
                  errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                />
              </FieldContent>
            </Field>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field, fieldState }) => (
            <Field data-disabled={loading || isSubmitting}>
              <FieldLabel>Description *</FieldLabel>
              <FieldContent>
                <Textarea
                  {...field}
                  rows={4}
                  className={cn(
                    "font-body resize-none",
                    fieldState.error &&
                      "border-(--color-destructive) focus-visible:ring-[var(--color-primary  )]"
                  )}
                  disabled={loading || isSubmitting}
                />
                <FieldError
                  errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                />
              </FieldContent>
            </Field>
          )}
        />

        <DialogFooter className="">
          <DialogClose asChild>
            <Button
              variant="outline"
              type="button"
              disabled={loading || isSubmitting}
              onClick={onCancel}
              className=""
            >
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" variant="" className="" disabled={loading || isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}


export default function EditItemDialog({ open, onOpenChange, item, fields, onSave, loading }) {
  // Only handle non-pathway edit logic here
  const [form, setForm] = useState(item || {})
  const [roles, setRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false)
  const [originalRoleIds, setOriginalRoleIds] = useState([])
  const { getToken } = useAuth()

  const fieldKeySignature = (fields || []).map((f) => f?.key).filter(Boolean).join("|")
  const hasRoleField = fieldKeySignature.includes("role")

  useEffect(() => {
    // Only sync when the dialog is actually open.
    // Many pages pass `fields` as an inline array, which changes identity every render.
    if (!open) return
    setForm(item || {})
  }, [open, item])

  useEffect(() => {
    if (open && hasRoleField) {
      setIsLoadingRoles(true)
      const fetchRoles = async () => {
        try {
          const token = await getToken()
          if (!token) return

          const reqConfig = {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          }

          const response = await axios.get(`${api_endpoint}/user/roles`, reqConfig)
          setRoles(response.data?.roles || [])

        } catch (error) {
          console.error("Error fetching roles:", error)
          toast.error("Failed to fetch roles. Please try again.")
          setRoles([])
        } finally {
          setIsLoadingRoles(false)
        }
      }
      fetchRoles()
    }
  }, [open, hasRoleField, getToken])

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true);
    try {
      const currentRoleIds = Array.isArray(form.role) ? form.role.map(r => String(r)) : [];
      const added_roles = currentRoleIds.filter(id => !originalRoleIds.includes(id));
      const deleted_roles = originalRoleIds.filter(id => !currentRoleIds.includes(id));

      let payload = { ...form, added_roles, deleted_roles };
      await onSave(payload)
    } finally {
      setIsSubmitting(false);
    }
  }

  const fieldKeys = (fields || []).map((f) => f.key)
  const isRoleEditDialog =
    fieldKeys.length === 2 && fieldKeys.includes("role") && fieldKeys.includes("description")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-auto">
        <DialogHeader className="font-heading text-(--neutral-900)">
          <DialogTitle className="font-heading text-(--neutral-900)">
            {isRoleEditDialog ? "Edit Role" : "Edit"}
          </DialogTitle>
        </DialogHeader>
        {isRoleEditDialog ? (
          <RoleEditForm
            item={item}
            loading={loading}
            onCancel={() => onOpenChange(false)}
            onSave={onSave}
          />
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.filter(field => !["title", "courses", "category"].includes(field.key)).map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium mb-1 font-heading text-(--neutral-900)">
                {`${field.label || field.key}${field.required ? " *" : ""}`}
              </label>
              {field.key === "role" && fields.length === 2 ? (
                <Input
                  value={form.role ?? ""}
                  onChange={e => handleChange("role", e.target.value)}
                  type="text"
                  required={field.required}
                />
              ) : field.key === "role" ? (
                <div>
                  <Select
                    onValueChange={value => {
                      const selectedRole = roles.find(r => String(r.id) === String(value));
                      if (value && selectedRole && !(form.role ?? []).some(r => String(r.roleId) === String(value))) {
                        handleChange("role", [...(form.role ?? []), { roleId: String(value), roleTitle: selectedRole.title }]);
                      }
                    }}
                    value=""
                    disabled={isLoadingRoles}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={isLoadingRoles ? "Loading roles..." : "Select all that apply"} />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(role => (
                        <SelectItem key={role.id} value={String(role.id)}>
                          {role.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {(Array.isArray(form.role) ? form.role : []).map(roleObj => {
                      // roleObj is {roleId, roleTitle}
                      const roleId = String(roleObj.roleId);
                      const roleTitle = roleObj.roleTitle;
                      // Fallback: look up in roles list if not present
                      const fallbackTitle = roles.find(r => String(r.id) === roleId)?.title;
                      return (
                        <div
                          key={roleId}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue/10 text-blue text-sm rounded-md border border-blue/20"
                        >
                          <span>{roleTitle || fallbackTitle || roleId}</span>
                          <button
                            type="button"
                            onClick={() => {
                              handleChange("role", (Array.isArray(form.role) ? form.role : []).filter(r => String(r.roleId) !== roleId));
                            }}
                            className="ml-1 text-blue hover:text-blue/70"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

              ) : (
                field.key === "description" || field.type === "textarea" ? (
                  <Textarea
                    value={form[field.key] ?? ""}
                    onChange={e => handleChange(field.key, e.target.value)}
                    rows={4}
                    required={field.required}
                  />
                ) : (
                  <Input
                    value={form[field.key] ?? ""}
                    onChange={e => handleChange(field.key, e.target.value)}
                    type={field.type || "text"}
                    required={field.required}
                  />
                )
              )}
            </div>
          ))}
          <DialogFooter className="">
            <DialogClose asChild>
              <Button
                variant="outline"
                type="button"
                disabled={loading}
                className=""
              >
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" variant="" className="" disabled={loading || isSubmitting}>
              {isSubmitting ? (
                <span className="flex items-center justify-center w-full">
                  <ClipLoader size={20} color="#fff" />
                </span>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog >
  )
}